import { sectionRenderer } from '@theme/section-renderer';

/**
 * A load more button for the product grid.
 *
 * The theme ships infinite scroll or numbered pages. Infinite scroll keeps pushing the
 * footer out of reach, which buries the newsletter and the policies and leaves keyboard
 * users with nowhere to land, so the shopper asks for the next page instead.
 *
 * Reuses the page that <results-list> has already prefetched when there is one, and falls
 * back to fetching the section itself.
 */
class MabLoadMore extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.#loadNextPage);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#loadNextPage);
  }

  get #resultsList() {
    return this.closest('results-list');
  }

  #loadNextPage = async () => {
    const grid = this.#resultsList?.querySelector('[ref="grid"]');
    const sectionId = this.getAttribute('section-id');
    const nextPage = Number(this.getAttribute('next-page'));
    const lastPage = Number(this.getAttribute('last-page'));

    if (!grid || !sectionId || !nextPage || nextPage > lastPage) return;

    const url = new URL(window.location.href);
    url.searchParams.set('page', String(nextPage));
    url.hash = '';

    if (this.button) {
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
    }

    try {
      const cached = this.#resultsList?.pages?.get(nextPage);
      const html = cached ?? (await sectionRenderer.getSectionHTML(sectionId, true, url));
      const cards = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[ref="grid"]')
        ?.querySelectorAll(':scope > [ref="cards[]"]');

      if (!cards?.length) {
        this.remove();
        return;
      }

      const firstNewCard = cards[0];
      grid.append(...cards);
      history.pushState('', '', url.toString());

      // Land the shopper on the first new piece rather than dropping them at the top.
      const focusTarget = firstNewCard.querySelector('a');
      if (focusTarget) {
        focusTarget.setAttribute('tabindex', '-1');
        focusTarget.focus({ preventScroll: true });
      }

      if (nextPage >= lastPage) {
        this.remove();
        return;
      }

      this.setAttribute('next-page', String(nextPage + 1));
    } finally {
      if (this.isConnected && this.button) {
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
      }
    }
  };
}

if (!customElements.get('mab-load-more')) {
  customElements.define('mab-load-more', MabLoadMore);
}
